import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import PushPermissionPrompt from './components/PushPermissionPrompt';
import api from './utils/api';
import { DistributorAuthProvider, useDistributorAuth } from './context/DistributorAuthContext';
import distributorApi from './utils/distributorApi';
import { SuperAdminAuthProvider, useSuperAdminAuth } from './context/SuperAdminAuthContext';

// ── تقسيم الحزمة (code-splitting) ─────────────────────────────────────────────
// كل صفحة تُحمَّل فقط عند فتحها فعلياً بدل تحميل كل الصفحات (بما فيها لوحة
// الأدمن الضخمة) دفعة واحدة عند أول فتح للتطبيق. هذا يسرّع ظهور أول صفحة
// (المحفظة غالباً) لأن حجم الكود المطلوب تحميله قبل الرسم الأول يصبح أصغر
// بكثير. لا تغيير في أي سلوك أو منطق — فقط توقيت تحميل الكود.
const Login                 = React.lazy(() => import('./pages/Login'));
const ForgotPassword        = React.lazy(() => import('./pages/ForgotPassword'));
const Wallet                = React.lazy(() => import('./pages/Wallet'));
const Recharge               = React.lazy(() => import('./pages/Recharge'));
const Packages               = React.lazy(() => import('./pages/Packages'));
const History                = React.lazy(() => import('./pages/History'));
const Games                  = React.lazy(() => import('./pages/Games'));
const Loyalty                = React.lazy(() => import('./pages/Loyalty'));
const Account                = React.lazy(() => import('./pages/Account'));
const Admin                  = React.lazy(() => import('./pages/Admin'));
const DistributorLogin       = React.lazy(() => import('./pages/DistributorLogin'));
const DistributorDashboard   = React.lazy(() => import('./pages/DistributorDashboard'));
const CardRedeem             = React.lazy(() => import('./pages/CardRedeem'));
const SuperAdminLogin        = React.lazy(() => import('./pages/SuperAdminLogin'));
const SuperAdminDashboard    = React.lazy(() => import('./pages/SuperAdminDashboard'));
const AuditLog                = React.lazy(() => import('./pages/AuditLog'));

// ── ErrorBoundary لصفحات React.lazy ──────────────────────────────────────────
// بدون هذا، فشل تحميل ملف أي صفحة (شبكة ضعيفة، أو ملف قديم مخزَّن بالمتصفح
// بعد نشر تحديث جديد) يترك شاشة بيضاء عالقة للأبد بلا أي رسالة ولا إعادة
// محاولة — لأن React يوقف رسم الشجرة بالكامل عند أي خطأ غير مُلتقَط أثناء
// Suspense/lazy. هذا المكوّن يلتقط ذلك الخطأ تحديداً:
//  - أول مرة يحصل الخطأ: إعادة تحميل تلقائية واحدة فقط (عبر علامة بـ
//    sessionStorage تُمسَح فور نجاح التحميل) — يحل أغلب الحالات فوراً
//    (نسخة قديمة مخزَّنة، غالباً تُصلَح بجلب الفهرس الجديد من الشبكة).
//  - لو تكرر الخطأ رغم إعادة التحميل (مشكلة شبكة فعلية مستمرة): يعرض
//    رسالة واضحة وزر "إعادة المحاولة" بدل شاشة بيضاء صامتة.
class LazyPageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch() {
    const RELOAD_FLAG = 'lazyPageAutoReloaded';
    if (!sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();
    }
  }
  componentDidMount() {
    // نجح الرسم هذه المرة (لم يلتقط أي خطأ) — امسح العلَم حتى يُسمح
    // بمحاولة إعادة تحميل تلقائية جديدة إذا حصل خطأ لاحقاً بجلسة مختلفة
    sessionStorage.removeItem('lazyPageAutoReloaded');
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="loading-screen" style={{ flexDirection: 'column', gap: 16, textAlign: 'center', padding: 24 }}>
          <div>تعذّر تحميل الصفحة — تحقق من اتصالك بالإنترنت</div>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            🔄 إعادة المحاولة
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Dark Mode Hook ────────────────────────────────────────────────────────────
export function useDarkMode() {
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);
  return [dark, setDark];
}

// ── قراءة التوكن من الرابط (لمتصفح الهوتسبوت) ────────────────────────────────
// يُستخدم مع رابط الدخول التلقائي المولَّد في صفحة الشحن (Recharge.js) لما
// يفتح المستخدم رابط المحفظة من متصفح آخر غير متصفح الواي فاي المقيّد.
// نخزّن access token و refresh token معاً حتى لو انتهت صلاحية الـ access
// token (15 دقيقة) قبل ما يفتح المستخدم الرابط، يتجدد تلقائياً عبر آلية
// التجديد الموجودة أصلاً في utils/api.js.
(function readTokenFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token        = params.get('auth_token');
    const refreshToken = params.get('refresh_token');
    if (token) {
      localStorage.setItem('token', decodeURIComponent(token));
      if (refreshToken) localStorage.setItem('refreshToken', decodeURIComponent(refreshToken));
      // احذف التوكن من الرابط بدون إعادة تحميل
      const clean = window.location.pathname + (window.location.hash || '');
      window.history.replaceState({}, '', clean);
    }
  } catch(e) {}
})();

// ── استبدال توكن OTT برابط جلسة حقيقية (لمتصفح الهوتسبوت) ────────────────────
// الجزء المقابل لما نولّده في Recharge.js (generateAndCopyAutoLoginLink).
// نتحقق أولاً من وجود ?ott= في الرابط فقط — لا يشتغل هذا الكود إطلاقاً في أي
// حالة أخرى ولا يؤثر على أي مستخدم عادي. إذا لم يكن موجوداً نُكمل فوراً بدون
// أي تأخير أو طلب شبكة إضافي.
function useOttRedeem() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ott = params.get('ott');
    if (!ott) { setReady(true); return; }

    api.get('/auth/ott/redeem', { params: { token: ott } })
      .then(res => {
        localStorage.setItem('token', res.data.token);
        if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
        if (res.data.user) localStorage.setItem('user', JSON.stringify(res.data.user));
      })
      .catch(() => {
        // الرابط منتهي الصلاحية أو مستخدَم مسبقاً — نكمل عادي، المستخدم يسجّل دخوله يدوياً
      })
      .finally(() => {
        window.history.replaceState({}, '', window.location.pathname + '#recharge');
        setReady(true);
      });
  }, []);
  return ready;
}

// ── Maintenance Screen ────────────────────────────────────────────────────────
function MaintenanceScreen({ msg }) {
  return (
    <div className="maintenance-screen">
      <div className="icon">🔧</div>
      <h1>الموقع تحت الصيانة</h1>
      <p>{msg || 'نعمل على تحسين الخدمة، نعود قريباً!'}</p>
      <button onClick={() => window.location.reload()}
        style={{ padding:'10px 28px', borderRadius:12, background:'var(--primary)', color:'white', border:'none', cursor:'pointer', fontFamily:'Tajawal', fontSize:14, fontWeight:700 }}>
        🔄 إعادة المحاولة
      </button>
    </div>
  );
}

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;
  return (user?.role === 'admin' || user?.role === 'subadmin') ? children : <Navigate to="/wallet" />;
};

const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? <Navigate to="/wallet" /> : children;
};

// ── حارس مسار الموزع — مستقل تماماً عن جلسة المستخدم العادي والمدير ────────
const DistributorPrivateRoute = ({ children }) => {
  const { distributor, loading } = useDistributorAuth();
  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;
  return distributor ? children : <Navigate to="/distributor/login" />;
};

// ── حارس مسار لوحة مالك المنصة — مستقل تماماً عن كل جلسات المستخدمين/الموزعين ──
const SuperAdminPrivateRoute = ({ children }) => {
  const { isAuthenticated } = useSuperAdminAuth();
  return isAuthenticated ? children : <Navigate to="/superadmin/login" />;
};

// ── جسر التنقل من إشعارات Push ──────────────────────────────────────────────
// لما المستخدم يضغط على إشعار والتطبيق مفتوح أصلاً، الـ service worker يرسل
// رسالة بالرابط المطلوب. هذا المكون يستقبلها وينقل داخل التطبيق عبر الراوتر
// (سلس وفوري، بدون أي إعادة تحميل كاملة تسبب "ومضة بيضاء").
function ServiceWorkerNavigationBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'NAVIGATE' && event.data.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  return null;
}

function AppInner() {
  const [maintenance, setMaintenance] = useState(null); // null=loading, false=ok, {msg}=maintenance
  const { user } = useAuth();
  const { distributor } = useDistributorAuth();
  const [dark, setDark] = useDarkMode();

  // توجيه تلقائي لصفحة الشحن إذا جاء من متصفح الهوتسبوت
  useEffect(() => {
    if (user && window.location.hash === '#recharge') {
      window.history.replaceState({}, '', window.location.pathname);
      window.location.href = '/recharge';
    }
  }, [user]);

  useEffect(() => {
    // مسارات الموزع والرابط العام مستقلة تماماً عن نظام المستخدمين — لا تتأثر بالصيانة
    const isDistributorFlow = window.location.pathname.startsWith('/distributor')
      || window.location.pathname.startsWith('/card/')
      || window.location.pathname.startsWith('/superadmin');
    if (isDistributorFlow) { setMaintenance(false); return; }
    // تحقق من وضع الصيانة (فقط للمستخدمين غير المدير)
    if (user?.role === 'admin' || user?.role === 'subadmin') { setMaintenance(false); return; }
    api.get('/app-config').then(r => {
      if (r.data?.maintenance_mode) {
        setMaintenance({ msg: r.data.maintenance_msg });
      } else {
        setMaintenance(false);
      }
    }).catch(() => setMaintenance(false));
    // ملاحظة: نعتمد على user?.id و user?.role تحديداً (مو كائن user كامل) —
    // لأن Layout.js يحدّث user بمرجع (reference) جديد بكل تنقّل بين الصفحات
    // حتى لو البيانات نفسها ما تغيّرت، وهذا كان يعيد تشغيل هذا الطلب بدون
    // داعٍ في كل صفحة يفتحها المستخدم. نفس السلوك الوظيفي محفوظ بالكامل:
    // لسا يعيد التحقق فوراً عند تسجيل الدخول/الخروج أو تغيّر الصلاحية.
  }, [user?.id, user?.role]);

  // تطبيق الثيم على كل شيء
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  }, [dark]);

  if (maintenance === null && user) return <div className="loading-screen"><div className="spinner"/></div>;
  if (maintenance && user?.role !== 'admin' && user?.role !== 'subadmin') return <MaintenanceScreen msg={maintenance.msg} />;

  return (
    <BrowserRouter>
      <ServiceWorkerNavigationBridge />
      {user && <PushPermissionPrompt />}
      {distributor && <PushPermissionPrompt apiClient={distributorApi} dismissKey="push_prompt_dismissed_at_distributor" />}
      <Toaster position="top-center" toastOptions={{
        style: { fontFamily: 'Tajawal, sans-serif', direction: 'rtl', background: dark ? '#22263a' : '#fff', color: dark ? '#e8eaf6' : '#1a202c' },
        duration: 3000
      }}/>
      <LazyPageErrorBoundary>
        <Suspense fallback={<div className="loading-screen"><div className="spinner"/></div>}>
          <Routes>
            <Route path="/login"           element={<PublicRoute><Login darkMode={dark} setDark={setDark} /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/" element={<PrivateRoute><Layout dark={dark} setDark={setDark} /></PrivateRoute>}>
              <Route index element={<Navigate to="/wallet" />} />
              <Route path="wallet"   element={<Wallet />} />
              <Route path="recharge" element={<Recharge />} />
              <Route path="packages" element={<Packages />} />
              <Route path="history"  element={<History />} />
              <Route path="games"    element={<Games />} />
              <Route path="loyalty"  element={<Loyalty />} />
              <Route path="account"  element={<Account />} />
            </Route>
            <Route path="/admin" element={<AdminRoute><Admin dark={dark} setDark={setDark} /></AdminRoute>} />
            <Route path="/distributor/login"     element={<DistributorLogin />} />
            <Route path="/distributor/dashboard" element={<DistributorPrivateRoute><DistributorDashboard /></DistributorPrivateRoute>} />
            <Route path="/card/:token"           element={<CardRedeem />} />
            <Route path="/superadmin/login"      element={<SuperAdminLogin />} />
            <Route path="/superadmin/dashboard"  element={<SuperAdminPrivateRoute><SuperAdminDashboard /></SuperAdminPrivateRoute>} />
            <Route path="/superadmin/audit-log"  element={<SuperAdminPrivateRoute><AuditLog /></SuperAdminPrivateRoute>} />
            <Route path="*" element={<Navigate to="/wallet" />} />
          </Routes>
        </Suspense>
      </LazyPageErrorBoundary>
    </BrowserRouter>
  );
}

export default function App() {
  const ready = useOttRedeem();
  if (!ready) return <div className="loading-screen"><div className="spinner"/></div>;
  return (
    <AuthProvider>
      <DistributorAuthProvider>
        <SuperAdminAuthProvider>
          <AppInner />
        </SuperAdminAuthProvider>
      </DistributorAuthProvider>
    </AuthProvider>
  );
}
